// project imports
import MainLayout from "layout/MainLayout";
import Loadable from "uicomponent/Loadable";
import { Navigate } from "react-router-dom";
import { ComponentErrorBoundary } from "components/ErrorBoundary";
import { useExperimentalMode } from "utils/experimentalMode";

// dashboard routing
const ProtectedRoute = Loadable(() => import("./ProtectedRouteWrapper"));
const DebugRouteWrapper = Loadable(() => import("./DebugRouteWrapper"));

const Clients = Loadable(() => import("views/clients"));

const ClientAddForm = Loadable(() => import("views/clients/add"));
const ClientView = Loadable(() => import("views/clients/view"));
const ClientEditForm = Loadable(() => import("views/clients/edit"));
const ClientSetupWizard = Loadable(() => import("views/clients/wizard"));

const Invoices = Loadable(() => import("views/invoices"));
const InvoiceEventsView = Loadable(() => import("views/invoices/events"));

const ErrorBoundary = Loadable(() => import("components/ErrorBoundary"));
const Dashboard = Loadable(() => import("views/dashboard"));
const DashboardV2 = Loadable(() => import("views/dashboard-v2"));
const Onboarding = Loadable(() => import("views/onboarding"));
const FinancialSettingsView = Loadable(() =>
  import("views/financial-settings")
);
const History = Loadable(() => import("views/history"));
const ManageSettings = Loadable(() => import("views/settings"));
const SettingsSmsPage = Loadable(() => import("views/settings/SMSSettingsPage"));
const AiTools = Loadable(() => import("views/ai"));
// const HelpContent = Loadable(() => import("views/help"));
const CalendarsView = Loadable(() => import("views/calendars"));
const SubscriptionView = Loadable(() => import("views/subscription"));
const DebugView = Loadable(() => import("views/debug"));
const OopsPage = Loadable(() => import("views/oops"));

import InvoiceAddForm from "views/invoices/add";
import InvoiceEditForm from "views/invoices/edit";
import InvoiceSettingsView from "views/invoice-settings";

function DefaultHome() {
  const experimental = useExperimentalMode();
  return (
    <ComponentErrorBoundary>
      {experimental ? <DashboardV2 /> : <Dashboard />}
    </ComponentErrorBoundary>
  );
}

// ==============================|| MAIN ROUTING ||============================== //

const AuthenticatedRoutes = {
  path: "/",
  element: (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  ),
  children: [
    {
      path: "/",
      element: <DefaultHome />,
    },
    {
      path: "/dashboard",
      element: (
        <ComponentErrorBoundary>
          <Dashboard />
        </ComponentErrorBoundary>
      ),
    },
    {
      path: "/dashboard-v2",
      element: (
        <ComponentErrorBoundary>
          <DashboardV2 />
        </ComponentErrorBoundary>
      ),
    },
    {
      path: "/history",
      element: <History />,
    },
    // Clients (specific paths must come before /clients/:id so they match correctly)
    {
      path: "/clients",
      element: (
        <ComponentErrorBoundary>
          <Clients />
        </ComponentErrorBoundary>
      ),
    },
    {
      path: "/clients/add",
      element: <ClientAddForm />,
    },
    {
      path: "/clients/wizard",
      element: (
        <ComponentErrorBoundary>
          <ClientSetupWizard />
        </ComponentErrorBoundary>
      ),
    },
    {
      path: "/clients/:id",
      element: <ClientView />,
    },
    {
      path: "/clients/:id/edit",
      element: <ClientEditForm />,
    },
    // Engagements
    {
      path: "/engagements",
      element: (
        <ComponentErrorBoundary>
          <Invoices />
        </ComponentErrorBoundary>
      ),
    },
    {
      path: "/engagements/:id",
      element: <InvoiceEventsView />,
    },
    {
      path: "/engagements/add",
      element: <InvoiceAddForm />,
    },
    {
      path: "/engagements/:id/edit",
      element: <InvoiceEditForm />,
    },
    {
      path: "/invoice-settings",
      element: <InvoiceSettingsView />,
    },
    //  Other. These are off for now.
    {
      path: "/ai",
      // element: <MagmaVoice />
      element: <AiTools />,
    },
    {
      path: "/settings",
      element: <ManageSettings />,
    },
    {
      path: "/settings/notifications/sms",
      element: <SettingsSmsPage />,
    },
    {
      path: "subscription",
      element: <SubscriptionView />,
    },
    {
      path: "financial-settings",
      element: <FinancialSettingsView />,
    },
    {
      path: "calendars",
      element: <CalendarsView />,
    },
    {
      path: "/oops",
      element: <OopsPage />,
    },
    // Hidden for now
    // {
    //   path: "/help/",
    //   element: <HelpContent />,
    // },
    {
      path: "/debug",
      element: (
        <DebugRouteWrapper>
          <ComponentErrorBoundary>
            <DebugView />
          </ComponentErrorBoundary>
        </DebugRouteWrapper>
      ),
    },
    {
      path: "undefined",
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: "error",
      element: <ErrorBoundary />,
    },
    {
      path: "*",
      element: <ErrorBoundary />,
    },
  ],
};

export default AuthenticatedRoutes;
